import type { Project } from '../content/projects.seed'

type Unknown = Record<string, unknown>

const localized = (value: unknown): value is { en: string; ru: string } =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as Unknown).en === 'string' &&
  typeof (value as Unknown).ru === 'string'

const isVariant = (value: unknown): boolean =>
  typeof value === 'object' && value !== null && typeof (value as Unknown).url === 'string'

const isMedia = (value: unknown): boolean => {
  if (typeof value !== 'object' || value === null) return false
  const item = value as Unknown
  const variants = item.variants as Unknown | undefined
  return (
    localized(item.alt) &&
    typeof item.width === 'number' &&
    typeof item.height === 'number' &&
    typeof variants === 'object' &&
    variants !== null &&
    isVariant(variants.webp) &&
    isVariant(variants.avif)
  )
}

const isLink = (value: unknown): boolean =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as Unknown).label === 'string' &&
  typeof (value as Unknown).url === 'string'

/**
 * `github` and `year` are nullable in the schema and only the seeder ever fills
 * them in, so a project created in the CRM or by the bot arrives without them.
 * They are normalised below rather than rejected.
 */
const isProject = (value: unknown): boolean => {
  if (typeof value !== 'object' || value === null) return false
  const item = value as Unknown
  return (
    typeof item.slug === 'string' &&
    typeof item.order === 'number' &&
    localized(item.title) &&
    localized(item.summary) &&
    localized(item.description) &&
    Array.isArray(item.technologies) &&
    item.technologies.every((entry) => typeof entry === 'string') &&
    Array.isArray(item.links) &&
    item.links.every(isLink) &&
    Array.isArray(item.media) &&
    item.media.every(isMedia)
  )
}

const normalize = (value: unknown): Project => {
  const item = value as Unknown
  const github = item.github as Unknown | null | undefined
  return {
    slug: item.slug as string,
    order: item.order as number,
    year: typeof item.year === 'number' ? item.year : null,
    title: item.title as Project['title'],
    summary: item.summary as Project['summary'],
    description: item.description as Project['description'],
    technologies: item.technologies as string[],
    links: item.links as Project['links'],
    media: item.media as Project['media'],
    github: {
      language: typeof github?.language === 'string' ? github.language : '',
      stars: typeof github?.stars === 'number' ? github.stars : 0,
      forks: typeof github?.forks === 'number' ? github.forks : 0,
      snapshotDate: typeof github?.snapshotDate === 'string' ? github.snapshotDate : '',
    },
  }
}

const parse = (payload: unknown[]): Project[] => {
  /*
   * One malformed record must not take the whole catalogue down — the terminal
   * shows what it can and reports the rest to the console.
   */
  const valid = payload.filter(isProject)
  if (valid.length !== payload.length) {
    console.warn(`Dropped ${payload.length - valid.length} malformed project record(s)`)
  }
  return valid.map(normalize).sort((a, b) => a.order - b.order)
}

/**
 * The seed catalogue, shown when the API is down. Imported lazily so the
 * happy path never pays for a second copy of the project data.
 */
export const bundledProjects = async (): Promise<Project[]> =>
  parse((await import('../../seed/projects.json')).default)

export async function getProjects(signal?: AbortSignal): Promise<Project[]> {
  const response = await fetch('/api/projects', { headers: { Accept: 'application/json' }, signal })
  if (!response.ok) throw new Error(`Projects request failed: ${response.status}`)

  const data: unknown = await response.json()
  const payload = Array.isArray(data) ? data : (data as { projects?: unknown }).projects
  if (!Array.isArray(payload)) throw new Error('Invalid projects response')
  return parse(payload)
}
